import React from "react";
import { Lotus, Mandala } from "@/components/decor/Motifs";
import { ArrowRight, Sparkles, MapPin, CalendarDays } from "lucide-react";
import { REGISTRATION_OPEN } from "@/config";

// Solo Krishna with flute — provided by user
const KRISHNA_IMG =
    "https://customer-assets.emergentagent.com/job_krishna-fest-portal/artifacts/7f35o921_image.png";

// Srila Prabhupada — Founder-Acharya of ISKCON (uploaded by user)
const PRABHUPADA_IMG =
    "https://customer-assets.emergentagent.com/job_krishna-fest-portal/artifacts/a4bnv1lo_image.png";

const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
    return (
        <section
            id="hero"
            className="relative min-h-screen w-full overflow-hidden"
            data-testid="hero-section"
            style={{
                background:
                    "radial-gradient(1200px 800px at 85% 40%, rgba(255,138,32,0.18) 0%, transparent 55%), radial-gradient(900px 700px at 10% 90%, rgba(212,175,55,0.14) 0%, transparent 55%), linear-gradient(160deg, #0A0F1F 0%, #14102A 45%, #1A0E24 100%)",
            }}
        >
            {/* Subtle grain */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />

            {/* Decorative mandalas */}
            <Mandala
                className="pointer-events-none absolute -top-40 -left-40 w-[560px] h-[560px] opacity-[0.10] animate-spin-slower"
                stroke="#D4AF37"
            />
            <Mandala
                className="pointer-events-none absolute -bottom-56 -right-56 w-[640px] h-[640px] opacity-[0.08] animate-spin-slow"
                stroke="#FF9933"
            />

            {/* Top ribbon */}
            <div className="relative z-30 flex items-center justify-between px-6 md:px-12 lg:px-[60px] pt-7">
                <div className="flex items-center gap-3">
                    <span className="grid place-items-center w-10 h-10 rounded-full border border-vgold-400/40 bg-vgold-400/5">
                        <Lotus className="w-5 h-5" />
                    </span>
                    <div className="leading-tight">
                        <p className="font-title text-sm md:text-base tracking-[0.35em] text-vgold-200">
                            IYS · SREEMANGAL
                        </p>
                        <p className="font-body text-[10px] uppercase tracking-[0.3em] text-vgold-100/60">
                            ISKCON Youth Services
                        </p>
                    </div>
                </div>
                <a
                    href="#register"
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToId("register");
                    }}
                    data-testid="hero-nav-register-link"
                    className="hidden md:inline-flex items-center gap-2 rounded-full border border-vgold-400/60 bg-white/5 backdrop-blur px-5 py-2 font-body text-xs font-semibold uppercase tracking-[0.3em] text-vgold-100 hover:bg-vgold-400/15 transition-all"
                >
                    Register <ArrowRight className="w-3.5 h-3.5" />
                </a>
            </div>

            {/* Main grid */}
            <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 lg:px-[60px] pt-10 md:pt-14 pb-40">
                <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-10 lg:gap-6">
                    {/* Left — text */}
                    <div className="lg:col-span-7 relative order-2 lg:order-1 animate-fade-up">
                        <div className="inline-flex items-center gap-2 rounded-full border border-vgold-400/40 bg-vgold-400/10 px-4 py-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-vgold-300" />
                            <span className="font-body text-[11px] uppercase tracking-[0.3em] text-vgold-200">
                                17 July 2026 · Sreemangal
                            </span>
                        </div>

                        <h1
                            className="mt-6 font-title font-black leading-[0.95] tracking-tight"
                            data-testid="hero-title"
                        >
                            <span className="block text-white text-3xl sm:text-4xl md:text-5xl xl:text-6xl">
                                ISKCON YOUTH
                            </span>
                            <span
                                className="block text-4xl sm:text-5xl md:text-6xl xl:text-7xl bg-gradient-to-r from-vgold-200 via-saffron-400 to-vgold-500 bg-clip-text text-transparent drop-shadow-[0_4px_24px_rgba(255,153,51,0.35)]"
                            >
                                MEGA FESTIVAL
                            </span>
                            <span className="block mt-2 text-3xl sm:text-4xl md:text-5xl xl:text-6xl text-white/95">
                                <span className="text-vgold-300">2026</span>
                            </span>
                        </h1>

                        <p className="mt-8 font-heading italic text-xl md:text-2xl text-cream-50/90 max-w-xl">
                            “Where the flute-song of the eternal awakens the youth of today.”
                        </p>

                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-cream-50/85 font-body text-sm md:text-base">
                            <span className="inline-flex items-center gap-2">
                                <CalendarDays className="w-4 h-4 text-vgold-300" />
                                17 July 2026 · 10 AM — 4 PM
                            </span>
                            <span className="h-4 w-px bg-vgold-400/30 hidden md:inline-block" />
                            <span className="inline-flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-vgold-300" />
                                Nitai Gaur Temple, Sobujbag
                            </span>
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <button
                                onClick={() => scrollToId("register")}
                                data-testid="hero-cta-register-btn"
                                className="group relative overflow-hidden rounded-full bg-gradient-to-r from-saffron-500 to-vgold-500 px-10 py-4 font-body font-bold uppercase tracking-[0.25em] text-inkbrown-900 shadow-[0_10px_40px_-10px_rgba(255,153,51,0.6)] transition-all hover:-translate-y-1 hover:shadow-[0_16px_60px_-12px_rgba(255,153,51,0.7)]"
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    {REGISTRATION_OPEN ? "Register — It’s FREE" : "See Event Details"}
                                    <ArrowRight className="w-4 h-4" />
                                </span>
                                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:animate-shimmer" />
                            </button>
                            <button
                                onClick={() => scrollToId("schedule")}
                                data-testid="hero-cta-schedule-btn"
                                className="rounded-full border border-vgold-400/60 bg-white/5 backdrop-blur px-8 py-3.5 font-body font-semibold uppercase tracking-[0.25em] text-vgold-100 hover:bg-vgold-400/15 transition-all"
                            >
                                View Schedule
                            </button>
                        </div>

                        {/* Stats strip */}
                        <div className="mt-10 grid grid-cols-3 max-w-md gap-6">
                            <StatMini value="৳ 0" label="Registration" />
                            <StatMini value="7+" label="Programmes" />
                            <StatMini value="∞" label="Blessings" />
                        </div>

                        {/* Prabhupada tribute — inline within left column */}
                        <a
                            href="#hero"
                            data-testid="prabhupada-tribute"
                            className="mt-10 inline-flex items-center gap-3 rounded-2xl glass-dark border border-vgold-400/40 hover:border-vgold-400/70 pl-2 pr-5 py-2 transition-all shadow-goldring group"
                        >
                            <span className="relative w-12 h-12 rounded-xl overflow-hidden border border-vgold-400/60 flex-shrink-0">
                                <img
                                    src={PRABHUPADA_IMG}
                                    alt="His Divine Grace A.C. Bhaktivedanta Swami Prabhupada"
                                    className="w-full h-full object-cover"
                                    data-testid="prabhupada-image"
                                />
                            </span>
                            <span className="leading-tight text-left">
                                <span className="block font-body text-[9px] uppercase tracking-[0.3em] text-vgold-300">
                                    Founder-Acharya
                                </span>
                                <span className="block font-heading text-base text-cream-50 whitespace-nowrap">
                                    His Divine Grace Srila Prabhupada
                                </span>
                            </span>
                        </a>
                    </div>

                    {/* Right — Krishna as devotional altar (full image, no cropping) */}
                    <div className="lg:col-span-5 relative order-1 lg:order-2 flex justify-center lg:justify-end animate-fade-up">
                        <div className="relative w-full max-w-[480px] mt-16 sm:mt-14 lg:mt-0">
                            {/* Halo / radiating rays behind the altar */}
                            <div
                                aria-hidden="true"
                                className="absolute -inset-16 opacity-40 animate-spin-slower pointer-events-none"
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, transparent 0deg, rgba(255,193,77,0.35) 8deg, transparent 16deg, transparent 30deg, rgba(255,193,77,0.28) 38deg, transparent 46deg, transparent 60deg, rgba(255,193,77,0.32) 68deg, transparent 76deg, transparent 90deg, rgba(255,193,77,0.28) 98deg, transparent 106deg, transparent 120deg, rgba(255,193,77,0.35) 128deg, transparent 136deg, transparent 150deg, rgba(255,193,77,0.28) 158deg, transparent 166deg, transparent 180deg, rgba(255,193,77,0.32) 188deg, transparent 196deg, transparent 210deg, rgba(255,193,77,0.28) 218deg, transparent 226deg, transparent 240deg, rgba(255,193,77,0.35) 248deg, transparent 256deg, transparent 270deg, rgba(255,193,77,0.28) 278deg, transparent 286deg, transparent 300deg, rgba(255,193,77,0.32) 308deg, transparent 316deg, transparent 330deg, rgba(255,193,77,0.28) 338deg, transparent 346deg, transparent 360deg)",
                                    maskImage:
                                        "radial-gradient(circle, black 30%, transparent 75%)",
                                    WebkitMaskImage:
                                        "radial-gradient(circle, black 30%, transparent 75%)",
                                }}
                            />
                            <div
                                aria-hidden="true"
                                className="absolute -inset-10 blur-3xl opacity-75 pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(ellipse 60% 55% at 50% 45%, rgba(255,193,77,0.6) 0%, rgba(255,138,32,0.35) 40%, rgba(255,138,32,0.05) 70%, transparent 82%)",
                                }}
                            />

                            {/* ISKCON crown on top — floats above the arch */}
                            <div className="absolute left-1/2 -translate-x-1/2 -top-16 md:-top-24 lg:-top-28 z-30 flex flex-col items-center">
                                <div
                                    className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 grid place-items-center rounded-full"
                                    style={{
                                        background:
                                            "radial-gradient(circle, rgba(255,193,77,0.45) 0%, rgba(255,193,77,0) 70%)",
                                    }}
                                >
                                    <img
                                        src="/images/iskcon-logo.png"
                                        alt="ISKCON"
                                        className="w-full h-full object-contain"
                                        style={{
                                            filter:
                                                "brightness(0) saturate(100%) invert(83%) sepia(41%) saturate(637%) hue-rotate(0deg) brightness(97%) contrast(96%) drop-shadow(0 0 12px rgba(255,193,77,0.6))",
                                        }}
                                        data-testid="hero-iskcon-logo"
                                    />
                                </div>
                                <span className="mt-1 block w-12 h-[2px] bg-gradient-to-r from-transparent via-vgold-400 to-transparent" />
                            </div>

                            {/* Outer ornate gold frame */}
                            <div
                                className="relative rounded-t-[220px] rounded-b-3xl p-[3px] shadow-[0_50px_140px_-20px_rgba(255,153,51,0.55)]"
                                style={{
                                    background:
                                        "linear-gradient(160deg, #F8DC8C 0%, #D4AF37 30%, #8F7220 55%, #D4AF37 80%, #F5C56A 100%)",
                                }}
                            >
                                {/* Middle band */}
                                <div className="rounded-t-[218px] rounded-b-[22px] p-[2px] bg-inkbrown-900">
                                    {/* Inner frame with subtle inner shadow */}
                                    <div
                                        className="relative rounded-t-[216px] rounded-b-[20px] overflow-hidden"
                                        style={{
                                            background:
                                                "linear-gradient(180deg, #1a0e24 0%, #2a1638 60%, #1a0e24 100%)",
                                            boxShadow:
                                                "inset 0 0 40px rgba(0,0,0,0.5)",
                                        }}
                                    >
                                        {/* Krishna image — natural aspect, never cropped */}
                                        <img
                                            src={KRISHNA_IMG}
                                            alt="Lord Krishna playing the divine flute"
                                            className="relative z-10 block w-full h-auto"
                                            data-testid="hero-krishna-image"
                                        />
                                        {/* Soft warm color grade to blend with page theme */}
                                        <div
                                            aria-hidden="true"
                                            className="absolute inset-0 pointer-events-none mix-blend-soft-light opacity-40"
                                            style={{
                                                background:
                                                    "linear-gradient(180deg, rgba(255,183,77,0.35) 0%, transparent 40%, rgba(90,10,60,0.4) 100%)",
                                            }}
                                        />
                                        {/* Bottom fade to blend into altar base */}
                                        <div
                                            aria-hidden="true"
                                            className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                                            style={{
                                                background:
                                                    "linear-gradient(180deg, transparent 0%, rgba(20,16,42,0.75) 100%)",
                                            }}
                                        />
                                        {/* Inner gold hairline */}
                                        <div className="pointer-events-none absolute inset-2 rounded-t-[210px] rounded-b-[16px] border border-vgold-200/25 z-20" />
                                    </div>
                                </div>
                            </div>

                            {/* Altar base — bronze pedestal beneath the arch */}
                            <div className="relative -mt-3 mx-auto w-[92%]">
                                <div
                                    className="h-4 rounded-t-md"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #F5C56A 0%, #B8860B 100%)",
                                    }}
                                />
                                <div
                                    className="h-6 border-t border-vgold-700/30"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #8F7220 0%, #5C4A15 100%)",
                                    }}
                                />
                                <div
                                    className="h-3 rounded-b-lg"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #B8860B 0%, #8F7220 100%)",
                                    }}
                                />
                            </div>

                            {/* Nameplate — Shri Krishna over the altar */}
                            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-30">
                                <div className="relative rounded-full bg-gradient-to-r from-vgold-500 via-saffron-400 to-vgold-500 px-7 py-1.5 shadow-[0_10px_28px_-10px_rgba(255,153,51,0.7)] border border-vgold-100/50">
                                    <span className="font-devanagari text-lg text-inkbrown-900 tracking-wider">
                                        श्री कृष्ण
                                    </span>
                                </div>
                            </div>

                            {/* Peacock feather — top-right, above the frame */}
                            <svg
                                className="absolute -top-6 -right-4 md:-right-8 w-24 h-28 opacity-90 animate-float z-20"
                                viewBox="0 0 80 100"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <defs>
                                    <radialGradient id="pfEye" cx="50%" cy="50%" r="50%">
                                        <stop offset="0%" stopColor="#7CD1FF" />
                                        <stop offset="35%" stopColor="#2E6DB4" />
                                        <stop offset="70%" stopColor="#F5C56A" />
                                        <stop offset="100%" stopColor="#C89828" />
                                    </radialGradient>
                                    <linearGradient id="pfLeaf" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#4A7C3A" />
                                        <stop offset="100%" stopColor="#0E3B18" />
                                    </linearGradient>
                                </defs>
                                <g transform="rotate(25 40 50)">
                                    <path
                                        d="M 40,95 C 30,60 20,40 40,10 C 60,40 50,60 40,95 Z"
                                        fill="url(#pfLeaf)"
                                    />
                                    <ellipse cx="40" cy="24" rx="10" ry="14" fill="url(#pfEye)" />
                                    <ellipse cx="40" cy="24" rx="4" ry="6" fill="#0B1E4A" />
                                </g>
                            </svg>

                            {/* Diya-like floating glows */}
                            <span className="absolute top-16 -left-4 w-3 h-3 rounded-full bg-vgold-300 shadow-[0_0_18px_6px_rgba(245,197,106,0.6)] animate-float" />
                            <span
                                className="absolute bottom-20 -right-4 w-2.5 h-2.5 rounded-full bg-saffron-400 shadow-[0_0_16px_5px_rgba(255,153,51,0.6)] animate-float"
                                style={{ animationDelay: "1.5s" }}
                            />
                            <span
                                className="absolute -bottom-8 left-10 w-2 h-2 rounded-full bg-vgold-200 shadow-[0_0_14px_5px_rgba(255,232,168,0.55)] animate-float"
                                style={{ animationDelay: "0.7s" }}
                            />

                            {/* Corner lotuses at the altar base */}
                            <Lotus className="absolute -bottom-10 -left-6 w-12 h-12 opacity-70 rotate-12" />
                            <Lotus
                                className="absolute -bottom-10 -right-6 w-12 h-12 opacity-70 -rotate-12"
                                fill="#FF9933"
                            />
                        </div>
                    </div>
                </div>

                {/* Bottom hint */}
                <div className="mt-16 hidden md:flex items-center justify-center gap-3 text-vgold-300/70">
                    <span className="h-px w-10 bg-vgold-400/40" />
                    <span className="font-devanagari text-lg">हरे कृष्ण</span>
                    <span className="h-px w-10 bg-vgold-400/40" />
                </div>
            </div>
        </section>
    );
}

function StatMini({ value, label }) {
    return (
        <div>
            <p className="font-title text-2xl md:text-3xl text-vgold-200">{value}</p>
            <p className="font-body text-[10px] uppercase tracking-[0.3em] text-cream-50/60 mt-1">
                {label}
            </p>
        </div>
    );
}
